import { useEffect, useState } from "react";
import { Badge } from "@zweb/ui";
import { apiFetch } from "../lib/api";

interface License {
  id: string;
  plan: string;
  status: string;
  maxUsers: number;
  currentUsers: number;
  features: string[];
  startsAt: string;
  expiresAt: string;
  autoRenew: boolean;
  createdAt: string;
}

const planLabels: Record<string, string> = {
  starter: "Starter",
  professional: "Professional",
  enterprise: "Enterprise",
};

const statusLabels: Record<string, string> = {
  active: "Ativa",
  expired: "Expirada",
  suspended: "Suspensa",
  pending: "Pendente",
};

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  expired: "bg-red-100 text-red-800",
  suspended: "bg-gray-100 text-gray-800",
  pending: "bg-yellow-100 text-yellow-800",
};

const planColors: Record<string, string> = {
  starter: "bg-green-100 text-green-800 border-green-200",
  professional: "bg-accent-100 text-accent-800 border-accent-200",
  enterprise: "bg-accent-100 text-accent-800 border-accent-200",
};

export default function LicensesPage() {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    apiFetch("/api/licenses")
      .then((json) => {
        if (json.success) {
          setLicenses(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("pt-BR");
  };

  const getDaysRemaining = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/dashboard" className="text-sm font-medium hover:text-accent-600">Dashboard</a>
            <a href="/chamados" className="text-sm font-medium hover:text-accent-600">Chamados</a>
            <a href="/downloads" className="text-sm font-medium hover:text-accent-600">Downloads</a>
            <a href="/licencas" className="text-sm font-medium text-accent-600">Licenças</a>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                window.location.href = "/";
              }}
              className="text-sm text-red-600 hover:underline"
            >
              Sair
            </button>
          </nav>
        </div>
      </header>

      <main className="container py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Licenças</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Gerencie suas licenças do ZWeb
            </p>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          {licenses.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-950">
              <p className="text-gray-600 dark:text-gray-400">
                Nenhuma licença encontrada. Entre em contato com nossa equipe comercial.
              </p>
              <a
                href="/solicitar-demo"
                className="mt-4 inline-block rounded-lg bg-accent-600 px-4 py-2 font-medium text-white hover:bg-accent-700"
              >
                Solicitar Demonstração
              </a>
            </div>
          ) : (
            licenses.map((license) => {
              const daysRemaining = license.expiresAt ? getDaysRemaining(license.expiresAt) : null;
              const isExpiringSoon = daysRemaining !== null && daysRemaining <= 30 && daysRemaining > 0;
              const isExpired = daysRemaining !== null && daysRemaining <= 0;

              return (
                <div
                  key={license.id}
                  className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-950"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-semibold">
                          Plano {planLabels[license.plan] || license.plan}
                        </h3>
                        <Badge className={statusColors[license.status]}>
                          {statusLabels[license.status]}
                        </Badge>
                        <Badge className={planColors[license.plan]}>
                          {planLabels[license.plan] || license.plan}
                        </Badge>
                      </div>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Início: {formatDate(license.startsAt)}
                        {license.expiresAt && ` • Expiração: ${formatDate(license.expiresAt)}`}
                      </p>
                    </div>

                    {isExpiringSoon && (
                      <div className="rounded-lg bg-yellow-50 px-4 py-2 text-sm text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400">
                        Expira em {daysRemaining} dias
                      </div>
                    )}
                    {isExpired && (
                      <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                        Expirada
                      </div>
                    )}
                  </div>

                  <div className="mt-6 grid gap-6 md:grid-cols-3">
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-900">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Usuários
                      </div>
                      <div className="mt-1 text-2xl font-bold">
                        {license.currentUsers} / {license.maxUsers}
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className="h-2 rounded-full bg-accent-600"
                          style={{
                            width: `${Math.min((license.currentUsers / license.maxUsers) * 100, 100)}%`,
                          }}
                        />
                      </div>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-900">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Renovação Automática
                      </div>
                      <div className="mt-1 text-2xl font-bold">
                        {license.autoRenew ? "Ativa" : "Inativa"}
                      </div>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-900">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Status
                      </div>
                      <div className="mt-1 text-2xl font-bold">
                        {statusLabels[license.status]}
                      </div>
                    </div>
                  </div>

                  {license.features && license.features.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Funcionalidades Incluídas
                      </h4>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {license.features.map((feature, index) => (
                          <span
                            key={index}
                            className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex gap-4">
                    <button className="rounded-lg bg-accent-600 px-4 py-2 font-medium text-white hover:bg-accent-700">
                      Renovar Licença
                    </button>
                    <button className="rounded-lg border border-gray-300 px-4 py-2 font-medium hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-900">
                      Alterar Plano
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}
